import { Bell, Search, HardHat, ClipboardCheck, Menu } from "lucide-react";
import { Button } from "@/shared/ui/button";
import { Input } from "@/shared/ui/input";
import { FactorySelector } from "@/core/components/FactorySelector";
import { useAppScope } from "@/core/AppScopeContext";
import { useSidebar } from "@/shared/ui/sidebar";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/shared/ui/tooltip";
import { cn } from "@/shared/lib/utils";

export function AppHeader() {
  const { effectiveMode, toggleModePreference, isSupervisor } = useAppScope();
  const mode = effectiveMode;
  const { toggleSidebar } = useSidebar();

  return (
    <header className="h-14 md:h-16 border-b border-border bg-card flex items-center justify-between px-3 md:px-6">
      {/* Left Section - Menu, Search & Mode Toggle */}
      <div className="flex items-center gap-2 md:gap-4 flex-1 min-w-0">
        {/* Mobile Menu Button */}
        <Button variant="ghost" size="icon" className="lg:hidden shrink-0" onClick={toggleSidebar}>
          <Menu className="w-5 h-5" />
        </Button>
        
        {/* Search - Hidden on mobile, visible on md+ */}
        <div className="relative flex-1 hidden md:block max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Search modules, reports, instructions..."
            className="pl-10 bg-muted/50 border-0 focus-visible:ring-1"
          />
        </div>
        
        {/* Mobile Search Button */}
        <Button variant="ghost" size="icon" className="md:hidden shrink-0">
          <Search className="w-5 h-5" />
        </Button>
        
        {/* Mode Toggle - Apple Style */}
        <div className="flex items-center gap-1.5 md:gap-2">
          {/* Operator Label - Icon only on mobile */}
          <span className={cn(
            "text-sm font-medium transition-colors flex items-center",
            mode === 'operator' ? "text-foreground" : "text-muted-foreground"
          )}>
            <HardHat className="w-4 h-4" />
            <span className="hidden sm:inline ml-1">Operator</span>
          </span>
          
          {/* Toggle Switch - Only enabled for supervisors */}
          {isSupervisor ? (
            <button
              onClick={toggleModePreference}
              className={cn(
                "relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
                mode === 'supervisor' ? "bg-primary" : "bg-muted"
              )}
              aria-label={`Switch to ${mode === 'operator' ? 'supervisor' : 'operator'} mode`}
            >
              <span
                className={cn(
                  "inline-block h-5 w-5 transform rounded-full bg-background shadow-lg transition-transform",
                  mode === 'supervisor' ? "translate-x-5" : "translate-x-0.5"
                )}
              />
            </button>
          ) : (
            <Tooltip>
              <TooltipTrigger asChild>
                <div
                  className="relative inline-flex h-6 w-11 shrink-0 items-center rounded-full bg-muted opacity-50 cursor-not-allowed"
                  aria-label="Supervisor mode requires a role assignment"
                >
                  <span className="inline-block h-5 w-5 transform rounded-full bg-background shadow-lg translate-x-0.5" />
                </div>
              </TooltipTrigger>
              <TooltipContent>
                <p>Supervisor mode requires a role assignment</p>
              </TooltipContent>
            </Tooltip>
          )}
          
          {/* Supervisor Label - Icon only on mobile */}
          <span className={cn(
            "text-sm font-medium transition-colors flex items-center",
            mode === 'supervisor' ? "text-foreground" : "text-muted-foreground",
            !isSupervisor && "opacity-50"
          )}>
            <span className="hidden sm:inline mr-1">Supervisor</span>
            <ClipboardCheck className="w-4 h-4" />
          </span>
        </div>
      </div>

      {/* Right Section - Actions */}
      <div className="flex items-center gap-1.5 md:gap-3">
        {/* Factory Selector */}
        <div className="hidden sm:block">
          <FactorySelector />
        </div>

        {/* Status Indicator - Hidden on small screens */}
        {/* <div className="hidden lg:flex items-center gap-2 px-3 py-1.5 rounded-full bg-success/10 text-success text-sm font-medium"> */}
          {/* <span className="w-2 h-2 rounded-full bg-success animate-pulse" /> */}
          {/* <span>Production Active</span> */}
        {/* </div> */}
        
        {/* Status Dot Only - Visible on md, hidden on lg+ */}
        <div className="hidden md:flex lg:hidden items-center justify-center w-8 h-8">
          <span className="w-2.5 h-2.5 rounded-full bg-success animate-pulse" title="Production Active" />
        </div>


        {/* Notifications */}
        <Button variant="ghost" size="icon" className="relative h-9 w-9 md:h-10 md:w-10">
          <Bell className="w-4 h-4 md:w-5 md:h-5" />
          <span className="absolute top-1.5 right-1.5 md:top-1 md:right-1 w-2 h-2 bg-destructive rounded-full" />
        </Button>


      </div>
    </header>
  );
}
